import { SmallCloseIcon } from '@chakra-ui/icons';
import {
    Box,
    Flex,
    IconButton,
    Popover,
    PopoverBody,
    PopoverContent,
    PopoverHeader,
    PopoverTrigger,
    Tooltip,
    useToast,
} from '@chakra-ui/react';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { downloadFile } from '../../canopy-ui-components/api/download';
import {
    buildUrlViaQueryString,
    removePaginationQueryParams,
} from '../../canopy-ui-components/api/newUtils';
import CanopyButton from '../../canopy-ui-components/components/CanopyButton/CanopyButton';
import { CanopyTableProps } from '../../canopy-ui-components/components/CanopyTable/CanopyTable';
import ActDl from '../../canopy-ui-components/components/Icons/ActDl';
import '../../canopy-ui-components/helpers/downloadFileHelper';
import { useCurrentColors } from '../../canopy-ui-components/hooks/UseCurrentColors/UseCurrentColors';
interface Props
    extends Pick<
        CanopyTableProps<any>,
        'downloadAllData' | 'downloadCurrentData' | 'globalProps'
    > {
    isDisabled?: boolean;
    endpoint?: string;
    endpointVersion?: string;
    endpointDomain?: string;
}
function CanopyTableDownloader({
    isDisabled,
    downloadAllData,
    downloadCurrentData,
    globalProps,
    endpoint,
}: any) {
    const currentColors = useCurrentColors();
    const [isLoading, setIsLoading] = useState(false);
    const { t, qp } = globalProps;
    const [isOpen, setIsOpen] = useState(false);
    const open = () => setIsOpen(!isOpen);
    const close = () => setIsOpen(false);
    const toast = useToast();
    const toastIdRef = React.useRef<any>(null);

    useEffect(() => {
        return () => {
            toast.closeAll();
        };
    }, []);

    const showToast = ({ status, color, downloadType }: any) => {
        if (navigator.onLine === false) {
            return false;
        }

        toast.closeAll();
        toastIdRef.current = toast({
            position: 'bottom-right',
            duration: ['error', 'success', 'not_found', 'initiated'].includes(
                status
            )
                ? 3000
                : null,
            isClosable: false,
            render: () => (
                <Flex
                    ml={'33px'}
                    color="white"
                    align="flex-start"
                    p={3}
                    justifyContent="space-between"
                    bg={color}
                    borderRadius="4px"
                    alignItems="center">
                    <Box>
                        {t(
                            `ui.mm.fee-report-${downloadType}-${
                                ['queued', 'processing'].includes(status)
                                    ? 'processing'
                                    : ['not_found'].includes(status)
                                    ? 'error'
                                    : status
                            }`
                        )}
                    </Box>
                    <IconButton
                        background="transparent"
                        cursor="pointer"
                        border="none"
                        icon={<SmallCloseIcon backgroundColor="transparent" />}
                        aria-label="sorted by descending"
                        onClick={closeToast}
                        variant="ghost"
                        _hover={{ backgroundColor: 'transparent' }}
                    />
                </Flex>
            ),
        });
    };

    const closeToast = () => {
        toast.close(toastIdRef.current);
    };
    const triggerApiCall = async (type: string) => {
        setIsLoading(true);
        try {
            let response: any = null;
            let url = null;
            const downloadFormat = [
                'SUMMARY_ALLDATA',
                'SUMMARY_CURRENT_DATA',
            ].includes(type)
                ? 'summary'
                : 'details';
            if (type === 'SUMMARY_ALLDATA' || type === 'DETAILS_ALLDATA') {
                showToast({
                    status:
                        downloadFormat === 'details'
                            ? 'initiated'
                            : 'processing',
                    color: 'blue.500',
                    downloadType: downloadFormat,
                });
                url = endpoint;
                const currentDate = moment().format('DD-MM-YYYY');
                let params: Record<string, unknown> = {};
                params['output_format'] = 'download_hash';
                if (type === 'DETAILS_ALLDATA') {
                    params['is_async'] = 'true';
                }
                params['from_date'] = '01-01-1940';
                params['to_date'] = currentDate;
                params['report_type'] = downloadFormat;
                response = await downloadFile({
                    url:
                        url &&
                        buildUrlViaQueryString(url?.split('?')[0], params),
                });
                if (downloadFormat === 'summary') {
                    if (response === true) {
                        showToast({
                            status: 'success',
                            color: 'green.500',
                            downloadType: downloadFormat,
                        });
                    } else {
                        showToast({
                            status: 'error',
                            color: currentColors.errorText,
                            downloadType: downloadFormat,
                        });
                    }
                }
            } else if (
                type === 'SUMMARY_CURRENT_DATA' ||
                type === 'DETAILS_CURRENT_DATA'
            ) {
                showToast({
                    status:
                        downloadFormat === 'details'
                            ? 'initiated'
                            : 'processing',
                    color: 'blue.500',
                    downloadType: downloadFormat,
                });
                url = endpoint && removePaginationQueryParams(endpoint);
                response = await downloadFile({
                    url: `${url}&${
                        type === 'DETAILS_CURRENT_DATA' ? 'is_async=true&' : ''
                    }output_format=download_hash&report_type=${downloadFormat}`,
                });

                if (downloadFormat === 'summary') {
                    if (response === true) {
                        showToast({
                            status: 'success',
                            color: 'green.500',
                            downloadType: downloadFormat,
                        });
                    } else {
                        showToast({
                            status: 'error',
                            color: currentColors.errorText,
                            downloadType: downloadFormat,
                        });
                    }
                }
            }
        } catch (err: any) {
            showToast({ status: 'error', color: currentColors.errorText });
        } finally {
            setIsLoading(false);
        }
    };

    const handleDownloadDetailedCurrentData: any = async () => {
        close();
        await triggerApiCall('DETAILS_CURRENT_DATA');
    };

    const handleDownloadSummaryCurrentData: any = async () => {
        close();
        await triggerApiCall('SUMMARY_CURRENT_DATA');
    };

    const handleDownloadDetailedAllData = async () => {
        close();
        await triggerApiCall('DETAILS_ALLDATA');
    };

    const handleDownloadSummaryAllData = async () => {
        close();
        await triggerApiCall('SUMMARY_ALLDATA');
    };

    if (downloadAllData && downloadCurrentData) {
        return (
            <Box position="relative">
                {' '}
                <Popover
                    placement="bottom"
                    closeOnBlur={true}
                    onClose={close}
                    isOpen={isOpen}>
                    {
                        <PopoverTrigger>
                            <Tooltip
                                aria-label="download"
                                label={t('ui.mm.tooltip.download')}
                                hasArrow>
                                <IconButton
                                    background="transparent"
                                    cursor="pointer"
                                    border="none"
                                    width="20px !important"
                                    minWidth="20px !important"
                                    height="20px !important"
                                    icon={<ActDl />}
                                    aria-label="download"
                                    backgroundColor="transparent"
                                    color={currentColors.brand['300']}
                                    variant="ghost"
                                    _hover={{
                                        backgroundColor: 'transparent',
                                    }}
                                    _focus={{
                                        border: '0px',
                                        backgroundColor: 'transparent',
                                        outline: 'none',
                                    }}
                                    _active={{
                                        border: '0px',
                                    }}
                                    isDisabled={isDisabled}
                                    onClick={open}
                                    isLoading={isLoading}></IconButton>
                            </Tooltip>
                        </PopoverTrigger>
                    }
                    <PopoverContent
                        backgroundColor={currentColors.componentBg}
                        boxShadow={`0px 0px 6px ${currentColors.boxShadow}`}
                        borderRadius="4px"
                        maxWidth="unset"
                        right="18px"
                        top="-27px"
                        width="160px"
                        _focus={{
                            border: '0px',
                            outline: 'none',
                        }}>
                        <PopoverHeader
                            fontWeight={'bold'}
                            fontSize="15px">
                            {t('ui.fee-report.download-summary')}
                        </PopoverHeader>
                        <PopoverBody
                            width="100%"
                            justifyContent="start">
                            <CanopyButton
                                width="100%"
                                fontWeight={'light'}
                                justifyContent="start"
                                fontSize="sm"
                                variant="ghost"
                                borderRadius="0"
                                color={currentColors.brand['10']}
                                _hover={{
                                    bg: currentColors.brand['950'],
                                }}
                                onClick={handleDownloadSummaryCurrentData}>
                                {t('current-results')}
                            </CanopyButton>
                            <CanopyButton
                                width="100%"
                                fontWeight={'light'}
                                justifyContent="start"
                                fontSize="sm"
                                variant="ghost"
                                borderRadius="0"
                                color={currentColors.brand['10']}
                                _hover={{
                                    bg: currentColors.brand['950'],
                                }}
                                onClick={handleDownloadSummaryAllData}>
                                {t('all-data')}
                            </CanopyButton>
                        </PopoverBody>

                        <PopoverHeader
                            fontWeight={'bold'}
                            fontSize="15px">
                            {t('ui.fee-report.detailed-report')}
                        </PopoverHeader>
                        <PopoverBody
                            width="100%"
                            justifyContent="start">
                            <CanopyButton
                                width="100%"
                                fontWeight={'light'}
                                justifyContent="start"
                                fontSize="sm"
                                variant="ghost"
                                borderRadius="0"
                                color={currentColors.brand['10']}
                                _hover={{
                                    bg: currentColors.brand['950'],
                                }}
                                onClick={handleDownloadDetailedCurrentData}>
                                {t('current-results')}
                            </CanopyButton>
                            <CanopyButton
                                width="100%"
                                fontWeight={'light'}
                                justifyContent="start"
                                fontSize="sm"
                                variant="ghost"
                                borderRadius="0"
                                color={currentColors.brand['10']}
                                _hover={{
                                    bg: currentColors.brand['950'],
                                    color: currentColors.popoverHoverColor,
                                }}
                                onClick={handleDownloadDetailedAllData}>
                                {t('all-data')}
                            </CanopyButton>
                        </PopoverBody>
                    </PopoverContent>
                </Popover>
            </Box>
        );
    } else {
        return (
            <Tooltip
                aria-label="download"
                label={t('ui.mm.tooltip.download')}
                hasArrow>
                <IconButton
                    background="transparent"
                    cursor="pointer"
                    border="none"
                    width="20px !important"
                    minWidth="20px !important"
                    height="20px !important"
                    icon={<ActDl />}
                    aria-label="download"
                    backgroundColor="transparent"
                    color={currentColors.brand['300']}
                    variant="ghost"
                    _hover={{
                        backgroundColor: 'transparent',
                    }}
                    _focus={{
                        border: '0px',
                        backgroundColor: 'transparent',
                        outline: 'none',
                    }}
                    _active={{
                        border: '0px',
                    }}
                    isDisabled={isDisabled}
                    isLoading={isLoading}
                    onClick={
                        downloadCurrentData
                            ? handleDownloadDetailedCurrentData
                            : handleDownloadDetailedAllData
                    }></IconButton>
            </Tooltip>
        );
    }
}
export default CanopyTableDownloader;
